import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  TrendingUp,
  Calendar,
  Users,
  Briefcase,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Receipt
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const RevenuesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterService, setFilterService] = useState('all');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockRevenues = [
        {
          id: 'REV-2024-001',
          client: 'محمد أحمد',
          clientType: 'فرد',
          service: 'استشارة قانونية',
          amount: 5000,
          date: '2024-12-01',
          source: 'invoice',
          invoiceId: 'INV-2024-001',
          caseId: 'WZ-2024-001',
          status: 'completed'
        },
        {
          id: 'REV-2024-002',
          client: 'شركة النور التجارية',
          clientType: 'شركة',
          service: 'تمثيل قضائي',
          amount: 15000,
          date: '2024-12-03',
          source: 'contract',
          invoiceId: null,
          caseId: 'WZ-2024-002',
          status: 'completed'
        },
        {
          id: 'REV-2024-003',
          client: 'فاطمة علي',
          clientType: 'فرد',
          service: 'صياغة عقد',
          amount: 3000,
          date: '2024-12-05',
          source: 'invoice',
          invoiceId: 'INV-2024-003',
          caseId: null,
          status: 'completed'
        },
        {
          id: 'REV-2024-004',
          client: 'خالد محمود',
          clientType: 'فرد',
          service: 'استشارة عقارية',
          amount: 2500,
          date: '2024-11-28',
          source: 'payment',
          invoiceId: null,
          caseId: 'WZ-2024-005',
          status: 'pending'
        },
        {
          id: 'REV-2024-005',
          client: 'مؤسسة الأعمال المتقدمة',
          clientType: 'شركة',
          service: 'خدمات قانونية شاملة',
          amount: 25000,
          date: '2024-11-20',
          source: 'contract',
          invoiceId: 'INV-2024-007',
          caseId: null,
          status: 'completed'
        },
        {
          id: 'REV-2024-006',
          client: 'سارة محمد',
          clientType: 'فرد',
          service: 'استشارة أسرية',
          amount: 1500,
          date: '2024-12-10',
          source: 'invoice',
          invoiceId: 'INV-2024-008',
          caseId: 'WZ-2024-010',
          status: 'expected'
        }
      ];
      setRevenues(mockRevenues);
      setLoading(false);
    }, 500);
  }, []);

  // Calculate statistics
  const stats = {
    total: revenues.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.amount, 0),
    monthly: revenues
      .filter(r => {
        const date = new Date(r.date);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear() && r.status === 'completed';
      })
      .reduce((sum, r) => sum + r.amount, 0),
    byClient: revenues
      .filter(r => r.status === 'completed')
      .reduce((acc, r) => {
        acc[r.client] = (acc[r.client] || 0) + r.amount;
        return acc;
      }, {}),
    byService: revenues
      .filter(r => r.status === 'completed')
      .reduce((acc, r) => {
        acc[r.service] = (acc[r.service] || 0) + r.amount;
        return acc;
      }, {}),
    expected: revenues.filter(r => r.status === 'expected').reduce((sum, r) => sum + r.amount, 0)
  };

  // Get top client and service
  const topClient = Object.entries(stats.byClient).sort((a, b) => b[1] - a[1])[0] || ['لا يوجد', 0];
  const topService = Object.entries(stats.byService).sort((a, b) => b[1] - a[1])[0] || ['لا يوجد', 0];

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: {
        label: 'مكتمل',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      pending: {
        label: 'قيد المعالجة',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      },
      expected: {
        label: 'متوقع',
        icon: TrendingUp,
        className: 'bg-blue-50 text-blue-700 border-blue-200'
      },
      cancelled: {
        label: 'ملغي',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Get source badge
  const getSourceBadge = (source) => {
    const sourceConfig = {
      invoice: { label: 'فاتورة', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      payment: { label: 'دفعة', className: 'bg-green-50 text-green-700 border-green-200' },
      contract: { label: 'عقد', className: 'bg-purple-50 text-purple-700 border-purple-200' }
    };

    const config = sourceConfig[source] || sourceConfig.payment;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // Filter revenues
  const filteredRevenues = revenues.filter(revenue => {
    const matchesSearch =
      revenue.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      revenue.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
      revenue.service.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || revenue.status === filterStatus;

    const matchesService = filterService === 'all' || revenue.service === filterService;

    // Period filter
    const matchesPeriod = (() => {
      if (filterPeriod === 'all') return true;
      const date = new Date(revenue.date);
      const now = new Date();
      
      if (filterPeriod === 'month') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }
      if (filterPeriod === 'year') {
        return date.getFullYear() === now.getFullYear();
      }
      return true;
    })();

    return matchesSearch && matchesStatus && matchesService && matchesPeriod;
  });

  // Handle delete
  const handleDelete = (revenueId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الإيراد؟')) {
      alert(`تم حذف الإيراد ${revenueId}`);
    }
  };

  // Handle export
  const handleExport = () => {
    alert('سيتم تصدير تقرير الإيرادات قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الإيرادات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <TrendingUp className="w-8 h-8" />
                <span>إدارة الإيرادات</span>
              </h1>
              <p className="text-gray-600">تتبع وإدارة جميع الإيرادات والمداخيل</p>
            </div>
            <div className="flex items-center gap-3">
              {hasPermission('finance.export') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
                >
                  <Download className="w-5 h-5" />
                  <span>تصدير</span>
                </button>
              )}
              {hasPermission('finance.revenue.create') && (
                <button
                  onClick={() => navigate('/admin/finance/revenues/create')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة إيراد</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 1️⃣ Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {/* Total Revenue */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الإيرادات</p>
                <p className="text-2xl font-black text-[#1E3A8A]">{stats.total.toLocaleString()} ر.س</p>
              </div>
              <DollarSign className="w-10 h-10 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Monthly Revenue */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الإيرادات الشهرية</p>
                <p className="text-2xl font-black text-green-600">{stats.monthly.toLocaleString()} ر.س</p>
              </div>
              <Calendar className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Top Client */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أعلى عميل</p>
                <p className="text-sm font-black text-[#C8A870]">{topClient[0].substring(0, 12)}...</p>
                <p className="text-xs text-gray-500 mt-1">{topClient[1].toLocaleString()} ر.س</p>
              </div>
              <Users className="w-10 h-10 text-[#C8A870] opacity-20" />
            </div>
          </div>

          {/* Top Service */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أعلى خدمة</p>
                <p className="text-sm font-black text-purple-600">{topService[0]}</p>
                <p className="text-xs text-gray-500 mt-1">{topService[1].toLocaleString()} ر.س</p>
              </div>
              <Briefcase className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </div>

          {/* Expected Revenue */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الإيرادات المتوقعة</p>
                <p className="text-2xl font-black text-blue-600">{stats.expected.toLocaleString()} ر.س</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* 3️⃣ Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم، عميل أو خدمة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الحالات</option>
                <option value="completed">مكتمل</option>
                <option value="pending">قيد المعالجة</option>
                <option value="expected">متوقع</option>
                <option value="cancelled">ملغي</option>
              </select>
            </div>

            {/* Period Filter */}
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الفترات</option>
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
              </select>
            </div>

            {/* Service Filter */}
            <div className="relative">
              <Briefcase className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterService}
                onChange={(e) => setFilterService(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الخدمات</option>
                <option value="استشارة قانونية">استشارة قانونية</option>
                <option value="تمثيل قضائي">تمثيل قضائي</option>
                <option value="صياغة عقد">صياغة عقد</option>
                <option value="خدمات قانونية شاملة">خدمات قانونية شاملة</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2️⃣ Revenues Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم العملية</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">العميل</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الخدمة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الإيراد</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">المصدر</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الفاتورة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRevenues.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد إيرادات</p>
                    </td>
                  </tr>
                ) : (
                  filteredRevenues.map((revenue) => (
                    <tr key={revenue.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#1E3A8A]">{revenue.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold text-gray-800">{revenue.client}</p>
                          <p className="text-xs text-gray-500">{revenue.clientType}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{revenue.service}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-green-600 text-lg">{revenue.amount.toLocaleString()} ر.س</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{revenue.date}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getSourceBadge(revenue.source)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {revenue.invoiceId ? (
                          <button
                            onClick={() => navigate(`/admin/finance/invoices/${revenue.invoiceId}`)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors"
                          >
                            <FileText className="w-4 h-4" />
                            {revenue.invoiceId}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(revenue.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {/* View */}
                          <button
                            onClick={() => alert('عرض تفاصيل الإيراد')}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>

                          {/* Edit */}
                          {hasPermission('finance.revenue.edit') && (
                            <button
                              onClick={() => navigate(`/admin/finance/revenues/${revenue.id}/edit`)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4 text-yellow-600" />
                            </button>
                          )}

                          {/* Delete */}
                          {hasPermission('finance.revenue.delete') && (
                            <button
                              onClick={() => handleDelete(revenue.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        {filteredRevenues.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">عرض {filteredRevenues.length} من أصل {revenues.length} إيراد</span>
              <span className="font-bold text-[#1E3A8A]">
                إجمالي الإيرادات المعروضة: {filteredRevenues.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.amount, 0).toLocaleString()} ر.س
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RevenuesPage;
